/**
 * 权限控制工具类
 *
 * 核心原则：
 * 1. permission_codes 是数组就用它（含空数组 = 零功能权限）
 * 2. user_type 仅展示，不参与鉴权
 * 3. 管理员全开仅当 role_codes 含 admin
 * 4. CollabCore demo（demo:*）不走 admin 旁路，必须矩阵显式勾选
 */

import { PermissionCode, PermissionGroup, type PermissionCodeType } from '@/constants/permission-codes'

export const Permission = PermissionCode
export type Permission = PermissionCodeType

/** 仅用于历史展示文案；禁止做功能开关 */
export enum UserType {
  ADMIN = 'admin',
  NORMAL = 'normal',
  RESTRICTED = 'restricted'
}

export interface RoleCodeRef {
  code: string
  name?: string
}

export interface UserInfo {
  id: number
  username: string
  name: string
  email: string
  user_type: UserType
  group_id?: number
  avatar?: string
  permission_codes?: string[]
  role_codes?: string[]
  roles?: RoleCodeRef[]
  permissions?: Record<string, boolean> | string[]
}

export interface MenuItem {
  key: string
  title: string
  path?: string
  icon?: string
  children?: MenuItem[]
  meta?: {
    permissions?: Permission[]
    hidden?: boolean
  }
}

const BACKEND_MENU_TO_PERMISSIONS: Record<string, readonly Permission[]> = {
  video_translation: PermissionGroup.VIDEO_PERMISSIONS,
  document_translation: PermissionGroup.DOCUMENT_TRANSLATION_PERMISSIONS,
  speech_translation: PermissionGroup.SPEECH_TRANSLATION_PERMISSIONS,
  classroom_view: PermissionGroup.CLASSROOM_PERMISSIONS,
  classroom_version_manage: [PermissionCode.CLASSROOM_VERSION_MANAGE],
  project_management: PermissionGroup.PROJECT_PERMISSIONS,
  user_management: [PermissionCode.SYSTEM_USER],
  system_management: PermissionGroup.SYSTEM_MANAGEMENT_PERMISSIONS,
  task_assignment: PermissionGroup.TASK_PERMISSIONS,
  group_management: [PermissionCode.SYSTEM_ORGANIZATION]
}

function isDemoPermission(permission: Permission | string): boolean {
  return String(permission).startsWith('demo:')
}

/** True when the required set is exclusively CollabCore demo codes. */
function isDemoOnlyPermissions(permissions: Permission[]): boolean {
  return permissions.length > 0 && permissions.every(isDemoPermission)
}

export class PermissionManager {
  private userInfo: UserInfo | null = null
  private userPermissions: Set<Permission> = new Set()

  setUser(user: UserInfo) {
    this.userInfo = user
    this.userPermissions = new Set(this.getUserPermissions(user))
  }

  private getUserPermissions(user: UserInfo): Permission[] {
    const codes = user.permission_codes
    if (Array.isArray(codes)) {
      return this.codesToPermissions(codes)
    }
    return this.normalizePermissionsFromApi(user.permissions)
  }

  private codesToPermissions(codes: string[]): Permission[] {
    const permValues = new Set<string>(Object.values(PermissionCode))
    const out: Permission[] = []
    for (const c of codes) {
      if (permValues.has(c)) out.push(c as Permission)
    }
    return out
  }

  private normalizePermissionsFromApi(permissions: Record<string, boolean> | string[] | undefined): Permission[] {
    if (!permissions) return []
    const seen = new Set<Permission>()
    if (Array.isArray(permissions)) {
      const permValues = new Set<string>(Object.values(PermissionCode))
      for (const c of permissions) {
        if (permValues.has(c)) seen.add(c as Permission)
      }
    } else {
      for (const [key, value] of Object.entries(permissions)) {
        if (value !== true) continue
        const mapped = BACKEND_MENU_TO_PERMISSIONS[key]
        if (mapped) for (const p of mapped) seen.add(p)
      }
    }
    return Array.from(seen)
  }

  hasPermission(permission: Permission): boolean {
    if (this._effectiveAdmin() && !isDemoPermission(permission)) return true
    return this.userPermissions.has(permission)
  }

  hasAnyPermission(permissions: Permission[]): boolean {
    if (permissions.length === 0) return true
    if (this._effectiveAdmin() && !isDemoOnlyPermissions(permissions)) return true
    return permissions.some(permission => this.userPermissions.has(permission))
  }

  hasAllPermissions(permissions: Permission[]): boolean {
    if (permissions.length === 0) return true
    if (this._effectiveAdmin() && !isDemoOnlyPermissions(permissions)) return true
    return permissions.every(permission => this.userPermissions.has(permission))
  }

  private _effectiveAdmin(): boolean {
    const info = this.userInfo
    if (!info) return false
    if (info.role_codes?.includes('admin')) return true
    if (info.roles?.some(role => role.code === 'admin')) return true
    return false
  }

  isAdmin(): boolean {
    return this._effectiveAdmin()
  }

  hasRole(role: UserType): boolean {
    if (role === UserType.ADMIN) return this._effectiveAdmin()
    return false
  }

  hasAnyRole(roles: UserType[]): boolean {
    return roles.some(role => this.hasRole(role))
  }

  filterMenuByPermission(menus: MenuItem[]): MenuItem[] {
    if (!this.userInfo) return []

    return menus.filter(menu => {
      const perms = menu.meta?.permissions as Permission[] | undefined
      if (perms && perms.length > 0 && !this.hasAnyPermission(perms)) {
        return false
      }

      if (menu.meta?.hidden) {
        return false
      }

      if (menu.children) {
        menu.children = this.filterMenuByPermission(menu.children)
        if (menu.children.length === 0 && !menu.path) {
          return false
        }
      }

      return true
    })
  }

  checkRoutePermission(route: { meta?: { permissions?: Permission[] } }): boolean {
    if (!this.userInfo) return false

    const meta = route.meta || {}
    const perms = meta.permissions as Permission[] | undefined
    if (!perms || perms.length === 0) return true

    // Admin bypass does not apply to demo:* — matrix must grant explicitly.
    if (this._effectiveAdmin() && !isDemoOnlyPermissions(perms)) return true

    return perms.some(permission => this.userPermissions.has(permission))
  }

  getCurrentUser(): UserInfo | null {
    return this.userInfo
  }

  getCurrentPermissions(): Permission[] {
    return Array.from(this.userPermissions)
  }

  clearUser() {
    this.userInfo = null
    this.userPermissions.clear()
  }
}

/** 头像/顶栏展示角色名；禁止用 user_type 映射成普通用户/受限用户。 */
export function displayRoleLabel(
  user: Pick<UserInfo, 'roles' | 'role_codes'> | null | undefined,
  emptyLabel = '',
): string {
  if (!user) return emptyLabel
  const names = (user.roles ?? [])
    .map((role) => role.name?.trim() || role.code)
    .filter((label): label is string => Boolean(label))
  if (names.length) return names.join('、')
  const codes = user.role_codes ?? []
  if (codes.length) return codes.join('、')
  return emptyLabel
}

export const permissionManager = new PermissionManager()

export function hasPermission(permission: Permission): boolean {
  return permissionManager.hasPermission(permission)
}

export function hasAnyPermission(permissions: Permission[]): boolean {
  return permissionManager.hasAnyPermission(permissions)
}

export function hasRole(role: UserType): boolean {
  return permissionManager.hasRole(role)
}

export function hasAnyRole(roles: UserType[]): boolean {
  return permissionManager.hasAnyRole(roles)
}

export function checkRoutePermission(route: { meta?: { permissions?: Permission[] } }): boolean {
  return permissionManager.checkRoutePermission(route)
}

export function filterMenuByPermission(menus: MenuItem[]): MenuItem[] {
  return permissionManager.filterMenuByPermission(menus)
}
